import { use, useEffect, useState } from 'react';

import { ResidentDevice } from 'features/residents/models/resident-device';
import ResidentSettingsProviderContext from 'features/residents/resident-settings/providers/contexts/resident-settings-provider-context';

type ResidentSettingDetailsDevices = {
  linkDevicesModalIsOpen: boolean;
  unlinkedDevices: ResidentDevice[];
  onDevicesLinkClicked: (selectedDevices: ResidentDevice[]) => void;
  onUnlinkDeviceClicked: (selectedDevice: ResidentDevice) => void;
  openLinkDevicesModal: () => void;
  closeLinkDevicesModal: () => void;
};

const useDetailsDevices = (): ResidentSettingDetailsDevices => {
  const {
    detailEditState: { viewingMode, residentSettingEditForm },
  } = use(ResidentSettingsProviderContext);

  const [linkDevicesModalIsOpen, setLinkDevicesModalIsOpen] = useState(false);
  const [unlinkedDevices, setUnlinkedDevices] = useState<ResidentDevice[]>([]);

  const onDevicesLinkClicked = (selectedDevices: ResidentDevice[]): void => {
    const findSelectedDeviceIndex = (unlinkedDevice: ResidentDevice): number =>
      selectedDevices.findIndex((selectedDevice) => selectedDevice.id === unlinkedDevice.id);

    setUnlinkedDevices((current) => current.filter((unlinkedDevice) => findSelectedDeviceIndex(unlinkedDevice) === -1));

    residentSettingEditForm.setValue('devices', [...residentSettingEditForm.getValues('devices')].concat(selectedDevices), {
      shouldDirty: true,
    });
    setLinkDevicesModalIsOpen(false);
  };

  const onUnlinkDeviceClicked = (selectedDevice: ResidentDevice): void => {
    setUnlinkedDevices((prev) => [...prev, selectedDevice]);
    residentSettingEditForm.setValue(
      'devices',
      [...residentSettingEditForm.getValues('devices')].filter((device) => device.id !== selectedDevice.id),
      { shouldDirty: true },
    );
  };

  const openLinkDevicesModal = (): void => {
    setLinkDevicesModalIsOpen(true);
  };

  const closeLinkDevicesModal = (): void => {
    setLinkDevicesModalIsOpen(false);
  };

  useEffect(() => {
    if (viewingMode === 'viewing') {
      setUnlinkedDevices([]);
    }
  }, [viewingMode]);

  return {
    linkDevicesModalIsOpen,
    unlinkedDevices,
    onDevicesLinkClicked,
    onUnlinkDeviceClicked,
    openLinkDevicesModal,
    closeLinkDevicesModal,
  };
};

export default useDetailsDevices;
